import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { LucideIcon } from 'lucide-react';
import { CornerDownLeft, CornerDownRight, LogOut, Monitor, Moon, Search, Sun } from 'lucide-react';
import { NAV_GROUPS, QUICK_ACTIONS } from '@/config/navigation';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useTheme } from '@/hooks/useTheme';
import { overlayClasses } from '@/components/ui/Dialog';
import { Kbd } from '@/components/ui/kbd';
import { cn } from '@/utils/cn';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Entry {
  /** Уникальный ключ и value для cmdk */
  id: string;
  /** Подпись в списке; по ней же идёт поиск */
  label: string;
  /** Префикс подписи («Создать:», «Документы /») */
  prefix?: string;
  keywords: string;
  group: string;
  icon?: LucideIcon;
  hint?: string;
  action: () => void;
}

const normalize = (text: string) => text.toLowerCase().replace(/ё/g, 'е');
const toTerms = (text: string) => normalize(text).split(/\s+/).filter(Boolean);

/**
 * Поиск без «нечётких» совпадений по буквам: каждое слово запроса должно встретиться целиком.
 * Совпадение в названии весит больше, чем в префиксе и ключевых словах; «ё» и «е» не различаются.
 */
const scoreEntry = (entry: Entry, terms: string[]) => {
  const name = normalize(entry.label);
  const prefix = normalize(entry.prefix ?? '');
  const extra = normalize(`${entry.keywords} ${entry.group} ${entry.hint ?? ''}`);
  let score = 0;
  for (const term of terms) {
    const index = name.indexOf(term);
    if (index === 0) score += 4;
    else if (index > 0) score += /[\s/·:-]/.test(name[index - 1]) ? 3 : 2;
    else if (prefix.includes(term)) score += 1.5;
    else if (extra.includes(term)) score += 1;
    else return 0;
  }
  return score / terms.length;
};

/** Найденные команды по убыванию совпадения (при равенстве — в порядке меню) */
const rankEntries = (entries: Entry[], terms: string[]) =>
  entries
    .map((entry, order) => ({ entry, order, score: scoreEntry(entry, terms) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || a.order - b.order)
    .map((r) => r.entry);

const itemClasses =
  'flex cursor-pointer select-none items-center gap-3 rounded-md px-2.5 py-2 text-sm text-foreground outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[disabled=true]:opacity-50 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0 [&>svg]:text-muted-foreground';

const groupClasses =
  '[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground';

/** Палитра команд (⌘K / Ctrl+K): переход к любому разделу, создание документов, тема, выход */
export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { state, logout } = useAuth();
  const { canOperate } = usePermissions();
  const { setTheme } = useTheme();
  const role = state.user?.role;
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState('');

  const entries = useMemo<Entry[]>(() => {
    const list: Entry[] = [];
    if (canOperate) {
      QUICK_ACTIONS.forEach((action) =>
        list.push({
          id: `create:${action.path}`,
          label: action.label,
          prefix: 'Создать:',
          keywords: 'создать новый',
          group: 'Создать',
          icon: action.icon,
          hint: action.hint,
          action: () => navigate(action.path),
        })
      );
    }
    NAV_GROUPS.forEach((group) =>
      group.items
        .filter((item) => !item.roles || (role && item.roles.includes(role)))
        .forEach((item) => {
          list.push({
            id: `nav:${item.path}`,
            label: item.label,
            keywords: item.keywords ?? '',
            group: group.label ?? 'Главное',
            icon: item.icon,
            action: () => navigate(item.path),
          });
          item.children
            ?.filter((child) => child.path !== item.path)
            .forEach((child) =>
              list.push({
                id: `nav:${child.path}`,
                label: child.label,
                prefix: `${item.label} /`,
                keywords: item.keywords ?? '',
                group: group.label ?? 'Главное',
                icon: CornerDownRight,
                action: () => navigate(child.path),
              })
            );
        })
    );
    list.push(
      { id: 'theme:light', label: 'Светлая тема', keywords: 'оформление', group: 'Оформление', icon: Sun, action: () => setTheme('light') },
      { id: 'theme:dark', label: 'Тёмная тема', keywords: 'ночная оформление', group: 'Оформление', icon: Moon, action: () => setTheme('dark') },
      { id: 'theme:system', label: 'Тема как в системе', keywords: 'системная оформление', group: 'Оформление', icon: Monitor, action: () => setTheme('system') },
      {
        id: 'account:logout',
        label: 'Выйти из системы',
        keywords: 'выход logout',
        group: 'Аккаунт',
        icon: LogOut,
        action: () => {
          logout();
          navigate('/login');
        },
      }
    );
    return list;
  }, [canOperate, role, navigate, setTheme, logout]);

  // С запросом — плоский список по убыванию совпадения; без запроса — разделы по группам
  const results = useMemo(() => {
    const terms = toTerms(search);
    return terms.length ? rankEntries(entries, terms) : null;
  }, [entries, search]);

  const grouped = useMemo(() => {
    const map = new Map<string, Entry[]>();
    entries.forEach((entry) => map.set(entry.group, [...(map.get(entry.group) ?? []), entry]));
    return [...map.entries()];
  }, [entries]);

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setSearch('');
      setSelected('');
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    // Новый запрос — выделяем лучший результат
    const terms = toTerms(value);
    const best = terms.length ? rankEntries(entries, terms)[0]?.id : entries[0]?.id;
    setSelected(best ?? '');
  };

  const run = (entry: Entry) => {
    handleOpenChange(false);
    entry.action();
  };

  const renderEntry = (entry: Entry, showGroup: boolean) => (
    <Command.Item
      key={entry.id}
      value={entry.id}
      onSelect={() => run(entry)}
      className={cn(itemClasses, entry.prefix?.endsWith('/') && !showGroup && 'pl-6')}
    >
      {entry.icon && <entry.icon />}
      <span className="min-w-0 truncate">
        {entry.prefix && <span className="text-muted-foreground">{entry.prefix} </span>}
        {entry.label}
      </span>
      {(showGroup || entry.hint) && (
        <span className="ml-auto shrink-0 truncate pl-3 text-xs text-muted-foreground">
          {showGroup ? entry.group : entry.hint}
        </span>
      )}
    </Command.Item>
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={overlayClasses} />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-[0.98] data-[state=open]:zoom-in-[0.98]"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">Поиск и команды</DialogPrimitive.Title>
          {/* Фильтрацию и сортировку делаем сами: встроенная сортировка групп в cmdk не срабатывает */}
          <Command
            label="Поиск и команды"
            loop
            shouldFilter={false}
            value={selected}
            onValueChange={setSelected}
            className="flex flex-col"
          >
            <div className="flex items-center gap-2.5 border-b px-4">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Command.Input
                autoFocus
                value={search}
                onValueChange={handleSearch}
                placeholder="Найти раздел или действие…"
                className="h-12 w-full border-0 bg-transparent p-0 text-[15px] shadow-none outline-none placeholder:text-muted-foreground focus:ring-0"
              />
              <Kbd className="hidden sm:inline-flex">Esc</Kbd>
            </div>
            <Command.List className="max-h-[min(60vh,420px)] overflow-y-auto overscroll-contain p-2">
              {results ? (
                results.length === 0 ? (
                  <div className="py-10 text-center text-sm text-muted-foreground">Ничего не найдено</div>
                ) : (
                  results.map((entry) => renderEntry(entry, true))
                )
              ) : (
                grouped.map(([group, items]) => (
                  <Command.Group key={group} heading={group} className={groupClasses}>
                    {items.map((entry) => renderEntry(entry, false))}
                  </Command.Group>
                ))
              )}
            </Command.List>
            <div className="flex items-center gap-4 border-t bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd>
                выбор
              </span>
              <span className="flex items-center gap-1.5">
                <Kbd>
                  <CornerDownLeft className="h-3 w-3" />
                </Kbd>
                открыть
              </span>
            </div>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
